import { Box, Button, Stack, Typography } from '@mui/material';
import type { ReactNode } from 'react';

export function PageHeader({
  title,
  subtitle,
  actions,
}: {
  title: string;
  subtitle?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      spacing={2}
      sx={{ alignItems: { sm: 'flex-end' }, justifyContent: 'space-between', mb: 3.5 }}
    >
      <Box>
        <Typography variant="h3">{title}</Typography>
        {subtitle && (
          <Typography color="text.secondary" sx={{ mt: 0.75 }}>
            {subtitle}
          </Typography>
        )}
      </Box>
      {actions && (
        <Stack direction="row" spacing={1.25}>
          {actions}
        </Stack>
      )}
    </Stack>
  );
}

/** Friendly empty state with an illustration-like icon tile and one primary action. */
export function EmptyState({
  icon,
  title,
  body,
  action,
}: {
  icon: ReactNode;
  title: string;
  body: ReactNode;
  action?: { label: string; onClick: () => void };
}) {
  return (
    <Box sx={{ textAlign: 'center', py: { xs: 6, sm: 10 }, px: 3 }}>
      <Box
        sx={(t) => {
          const p = t.vars?.palette.primary.main ?? t.palette.primary.main;
          return {
            width: 72,
            height: 72,
            mx: 'auto',
            mb: 2.5,
            borderRadius: '20px',
            display: 'grid',
            placeItems: 'center',
            color: 'primary.main',
            background: `linear-gradient(135deg, color-mix(in srgb, ${p} 16%, transparent), color-mix(in srgb, ${p} 4%, transparent))`,
            border: `1px solid color-mix(in srgb, ${p} 18%, transparent)`,
            '& svg': { fontSize: 34 },
          };
        }}
      >
        {icon}
      </Box>
      <Typography variant="h5" sx={{ mb: 1 }}>
        {title}
      </Typography>
      <Typography color="text.secondary" sx={{ maxWidth: 420, mx: 'auto' }}>
        {body}
      </Typography>
      {action && (
        <Button variant="contained" sx={{ mt: 3 }} onClick={action.onClick}>
          {action.label}
        </Button>
      )}
    </Box>
  );
}
